import { useInternationalization } from "@/lib/contexts";
import { useEffect, useRef, useState } from "react";
import { MAP_CONFIG } from "./map-location.constants";

interface LeafletLatLng {
  lat: number;
  lng: number;
}

interface LeafletMarkerLike {
  setLatLng: (coordinates: [number, number]) => void;
}

interface LeafletMapLike {
  getCenter: () => LeafletLatLng;
  getZoom: () => number;
  setView: (coordinates: [number, number], zoom: number) => void;
  on: (eventName: string, handler: () => void) => void;
  invalidateSize: () => void;
  remove: () => void;
}

interface UseMapLocationProps {
  latitude: number;
  longitude: number;
  interactionMode: "read" | "edit";
  onCoordinatesChange: (lat: number, lng: number) => void;
}

export const useMapLocation = ({
  latitude,
  longitude,
  interactionMode,
  onCoordinatesChange,
}: UseMapLocationProps) => {
  const { t } = useInternationalization("pages.rooms.wizard.steps.location-step");
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<LeafletMapLike | null>(null);
  const markerRef = useRef<LeafletMarkerLike | null>(null);
  const interactionModeRef = useRef(interactionMode);
  const onCoordinatesChangeRef = useRef(onCoordinatesChange);
  const isDraggingRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const invalidateSizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [currentLat, setCurrentLat] = useState(latitude);
  const [currentLng, setCurrentLng] = useState(longitude);
  const [isLoading, setIsLoading] = useState(true);
  const [isLeafletLoaded, setIsLeafletLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    interactionModeRef.current = interactionMode;
  }, [interactionMode]);

  useEffect(() => {
    onCoordinatesChangeRef.current = onCoordinatesChange;
  }, [onCoordinatesChange]);

  // Cargar Leaflet solo una vez al montar el componente
  useEffect(() => {
    if (window.L) {
      setIsLeafletLoaded(true);
      return;
    }

    const loadLeaflet = () => {
      // Cargar CSS
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = MAP_CONFIG.LEAFLET.CSS_URL;
      link.integrity = MAP_CONFIG.LEAFLET.CSS_INTEGRITY;
      link.crossOrigin = "";
      document.head.appendChild(link);

      // Cargar JS
      const script = document.createElement("script");
      script.src = MAP_CONFIG.LEAFLET.JS_URL;
      script.integrity = MAP_CONFIG.LEAFLET.JS_INTEGRITY;
      script.crossOrigin = "";
      script.async = true;
      script.onload = () => setIsLeafletLoaded(true);
      script.onerror = () => {
        console.error("Error loading Leaflet library");
        setError(t("error_loading_map"));
        setIsLoading(false);
      };
      document.head.appendChild(script);
    };

    loadLeaflet();
  }, [t]);

  // Inicializar el mapa cuando Leaflet esté listo
  useEffect(() => {
    if (!isLeafletLoaded || !mapRef.current || leafletMapRef.current) return;

    const initializeMap = () => {
      try {
        const map = window.L.map(mapRef.current, {
          center: [latitude, longitude],
          zoom: MAP_CONFIG.ZOOM_LEVEL,
          zoomControl: true,
        });

        window.L.tileLayer(MAP_CONFIG.TILES.URL, {
          attribution: MAP_CONFIG.TILES.ATTRIBUTION,
          maxZoom: MAP_CONFIG.MAX_ZOOM,
        }).addTo(map);

        markerRef.current = window.L.marker([latitude, longitude], {
          icon: window.L.icon({
            iconUrl: "/images/location.png",
            iconSize: [120, 75],
            iconAnchor: [48, 60],
          }),
          interactive: false,
          keyboard: false,
        }).addTo(map);

        leafletMapRef.current = map;

        map.on("move", () => {
          if (interactionModeRef.current !== "edit") return;

          if (!isDraggingRef.current) {
            isDraggingRef.current = true;
          }

          if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
          }

          rafRef.current = requestAnimationFrame(() => {
            const center = map.getCenter();
            markerRef.current?.setLatLng([center.lat, center.lng]);
            setCurrentLat(center.lat);
            setCurrentLng(center.lng);
            rafRef.current = null;
          });
        });

        map.on("moveend", () => {
          if (interactionModeRef.current !== "edit") return;

          isDraggingRef.current = false;

          if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
          }

          const center = map.getCenter();
          onCoordinatesChangeRef.current(center.lat, center.lng);
        });

        // Invalidar tamaño después de un pequeño delay para asegurar que el contenedor está renderizado
        invalidateSizeTimeoutRef.current = setTimeout(() => {
          map.invalidateSize();
          invalidateSizeTimeoutRef.current = null;
        }, 100);

        setIsLoading(false);
      } catch (error) {
        console.error("Error initializing map:", error);
        setError(t("error_initializing_map"));
        setIsLoading(false);
      }
    };

    initializeMap();
  }, [isLeafletLoaded, latitude, longitude, t]);

  useEffect(() => {
    if (!leafletMapRef.current || !latitude || !longitude) return;
    if (isDraggingRef.current) return;

    markerRef.current?.setLatLng([latitude, longitude]);

    const currentCenter = leafletMapRef.current.getCenter();
    const latDiff = Math.abs(currentCenter.lat - latitude);
    const lngDiff = Math.abs(currentCenter.lng - longitude);

    if (latDiff > MAP_CONFIG.UPDATE_THRESHOLD || lngDiff > MAP_CONFIG.UPDATE_THRESHOLD) {
      leafletMapRef.current.setView([latitude, longitude], leafletMapRef.current.getZoom());
    }

    if (interactionMode === "edit") {
      const center = leafletMapRef.current.getCenter();
      setCurrentLat(center.lat);
      setCurrentLng(center.lng);
    } else {
      setCurrentLat(latitude);
      setCurrentLng(longitude);
    }
  }, [interactionMode, latitude, longitude]);

  // Invalidar tamaño del mapa cuando el contenedor cambia de dimensiones
  useEffect(() => {
    if (!leafletMapRef.current || !mapRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (leafletMapRef.current) {
        leafletMapRef.current.invalidateSize();
      }
    });

    resizeObserver.observe(mapRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Limpieza al desmontar el componente
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }

      if (invalidateSizeTimeoutRef.current !== null) {
        clearTimeout(invalidateSizeTimeoutRef.current);
        invalidateSizeTimeoutRef.current = null;
      }

      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }

      markerRef.current = null;
    };
  }, []);

  return {
    mapRef,
    currentLat,
    currentLng,
    isLoading,
    error,
    t,
  };
};
